import React, { useState, useRef } from "react";
import {
  Stack,
  TextField,
  Paper,
  ListItem,
  Chip,
  Button,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import SearchArticlesModalResult from "./SearchArticlesModalResult";
import { useSelector } from "react-redux";

import "./css/SearchArticle.css";

function SearchArticles() {
  const { t } = useTranslation(["Articles"]);
  const [searchList, setSearchList] = useState([]);
  const [word, setWord] = useState("");
  const ref = useRef(null);
  const [searchArticle, setSearchArticle] = useState([]);
  const [open, setOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const userState = useSelector((state) => state.user);

  const getDefaultDateRange = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - 1);

    const formatDate = (date) => date.toISOString().split("T")[0];

    return {
      start: formatDate(startDate),
      end: formatDate(endDate),
    };
  };

  const defaultDates = getDefaultDateRange();
  const [publishDate, setPublishDate] = useState(defaultDates.start);
  const [endPublishDate, setEndPublishDate] = useState(defaultDates.end);

  const handleSpace = (e) => {
    if (e.keyCode === 13 && word.trim() !== "") {
      setSearchList((prev) => [...prev, word.trim()]);
      setWord("");
      ref.current.value = "";
    }
  };

  const handleDelete = (chipToDelete) => {
    setSearchList((chips) => chips.filter((chip) => chip !== chipToDelete));
  };

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const handleSearch = async () => {
    try {
      setIsSearching(true);
      await delay(800); // Optional: slight wait to enhance UX

      if (searchList.length > 0) {
        await handleSearchByKeywords();
      } else {
        await handleSearchByDate();
      }
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchByKeywords = async () => {
    const data = {
      username: userState.username,
      keywords: searchList,
      publicationDate: publishDate || null,
      endPublicationDate: endPublishDate || null,
    };

    await fetch(
      `${process.env.REACT_APP_API_URL}/article/SearchArticleByKeyWords`,
      {
        method: "POST",
        withCredentials: true,
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userState.token}`,
        },
        body: JSON.stringify(data),
      }
    )
      .then((results) => results.json())
      .then((data) => {
        setSearchArticle(data.length > 0 ? data : []);
        setOpen(true);
      });
  };

  const handleSearchByDate = async () => {
    const data = {
      username: userState.username,
      publicationDate: publishDate || null,
      endPublicationDate: endPublishDate || null,
    };

    await fetch(`${process.env.REACT_APP_API_URL}/article/SearchArticleByDate`, {
      method: "POST",
      withCredentials: true,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userState.token}`,
      },
      body: JSON.stringify(data),
    })
      .then((results) => results.json())
      .then((data) => {
        setSearchArticle(data);
        setOpen(true);
      });
  };

  return (
    <>
      <Stack spacing={2} direction="row" className="Filter__Search">
        <div className="Filter__date">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="Filter__date__datePicker">
              <label>{t("StartDate")}</label>
              <DatePicker
                value={dayjs(publishDate)}
                onChange={(newValue) => setPublishDate(newValue)}
              />
              <label>{t("EndDate")}</label>
              <DatePicker
                value={dayjs(endPublishDate)}
                onChange={(newValue) => setEndPublishDate(newValue)}
              />
            </div>
          </LocalizationProvider>
        </div>

        <TextField
          margin="normal"
          name="search"
          label={t("Search")}
          type="text"
          id="search"
          onChange={(e) => setWord(e.currentTarget.value)}
          onKeyDown={handleSpace}
          value={word}
          inputRef={ref}
        />

        {searchList.length > 0 && (
          <Paper
            sx={{
              display: "flex",
              justifyContent: "center",
              flexWrap: "wrap",
              listStyle: "none",
              p: 0.5,
              m: 0,
            }}
            component="ul"
          >
            {searchList.map((data, index) => (
              <ListItem key={index}>
                <Chip label={data} onDelete={() => handleDelete(data)} />
              </ListItem>
            ))}
          </Paper>
        )}

        <Button
          variant="contained"
          sx={{ backgroundColor: "#FFBC0F", color: "black" }}
          onClick={handleSearch}
          disabled={isSearching}
          startIcon={
            isSearching ? <CircularProgress size={20} color="inherit" /> : null
          }
        >
          {isSearching ? t("Searching...") : t("Search")}
        </Button>
      </Stack>

      <SearchArticlesModalResult
        open={open}
        close={() => setOpen(false)}
        searchArticle={searchArticle}
      />

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: 9999,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
        open={isSearching}
      >
        <CircularProgress color="primary" size={60} />
      </Backdrop>
    </>
  );
}

export default SearchArticles;
